/**
 * 微信客服消息 SSE 连接管理 Hook
 *
 * 建立与后端 SSE 端点的持久连接，监听 new_message 事件
 * 页面卸载时自动断开
 */

import { useEffect, useRef, useState, useCallback } from 'react';

/** SSE 新消息事件数据 */
export interface SseNewMessage {
  openid: string;
  msgType: string;
  content: string;
  timestamp: string;
}

/** SSE 连接状态 */
type SseStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

/** SSE 端点 */
const SSE_URL = '/api/wechat/sse/messages';

interface UseWechatSseOptions {
  /** 收到新消息时的回调 */
  onNewMessage?: (message: SseNewMessage) => void;
  /** 是否启用（默认 true） */
  enabled?: boolean;
}

interface UseWechatSseReturn {
  /** 连接状态 */
  status: SseStatus;
  /** 最后收到的消息 */
  lastMessage: SseNewMessage | null;
  /** 手动重连 */
  reconnect: () => void;
}

export function useWechatSse(options: UseWechatSseOptions = {}): UseWechatSseReturn {
  const { onNewMessage, enabled = true } = options;
  const [status, setStatus] = useState<SseStatus>('disconnected');
  const [lastMessage, setLastMessage] = useState<SseNewMessage | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const onNewMessageRef = useRef(onNewMessage);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);

  // 保持 callback ref 同步
  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  }, [onNewMessage]);

  const cleanup = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    cleanup();
    if (!enabled) return;

    setStatus('connecting');

    const es = new EventSource(SSE_URL, { withCredentials: true });
    eventSourceRef.current = es;

    es.onopen = () => {
      setStatus('connected');
      reconnectAttemptsRef.current = 0;
    };

    // 监听 new_message 事件
    es.addEventListener('new_message', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data) as SseNewMessage;
        setLastMessage(data);
        onNewMessageRef.current?.(data);
      } catch {
        console.warn('[SSE] Failed to parse new_message event:', event.data);
      }
    });

    // 心跳事件（忽略）
    es.addEventListener('heartbeat', () => {
      // 心跳保活，无需处理
    });

    es.onerror = () => {
      setStatus('error');
      es.close();
      eventSourceRef.current = null;

      // 自动重连（指数退避，最多 30 秒）
      const attempt = reconnectAttemptsRef.current;
      const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
      reconnectAttemptsRef.current = attempt + 1;

      reconnectTimerRef.current = setTimeout(() => {
        if (enabled) {
          connect();
        }
      }, delay);
    };
  }, [enabled, cleanup]);

  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      cleanup();
      setStatus('disconnected');
    }
    return cleanup;
  }, [enabled, connect, cleanup]);

  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  return { status, lastMessage, reconnect };
}
